import React, { useState, useEffect, useRef } from 'react'
import styles from './styles.module.scss'
import { useTranslation } from 'react-i18next'
import parentStyles from '../../styles.module.scss'
import Icon from 'srcReact/components/icons/Icon'
import { useDispatch, useSelector } from 'react-redux'
import { messengerActions } from 'srcReact/MessengerPage'
import ReplyingToMessage from '../ReplyingToMessage'
import RichTextEditor from 'srcReact/components/common2/RichTextEditor'
import { Alert } from 'srcReact/components/common2/Alert'
import toast, { Toaster } from 'react-hot-toast'
import { messengerSettings } from 'srcReact/MessengerPage/MessengerSettings'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import ErrorMessage from 'srcReact/components/common2/ErrorMessage'
import { commonActions } from 'srcReact/components'
import { proceesImagesWithIK, separateFilesByType } from 'srcReact/helpers/fileHelper'
import IconButton from 'srcReact/components/common2/IconButton'
import Spinner from 'srcReact/components/common2/Spinner'
import { uuid } from 'srcReact/helpers/utils'

export default function InputArea({
  isMobile,
  isEditMode,
  editEditorId,
  handleExitEditMode,
  handleEdit,
  existingMessage,
  editEditorRef,
  scrollToTarget,
  focusedMode,
  setFocusedMode
}) {
  const { t } = useTranslation()
  const sendButtonRef = useRef(null)
  const editorRef = useRef('newMessage')
  const dispatch = useDispatch()
  const {
    common: {
      tagUsersList,
      filesCreation //{ fileIds, loadingFile, error: fileUploadError, success: fileUploadSuccess }
    },
    login: { user: loggedInUser },
    messenger: {
      openedConversation,
      originMessage,
      organizationId,
      loadingSendMessage,
      isMessagePosted,
      loadingEditMessage,
      conversation,
      pendingMessages,
      messageTags: { loading: tagsLoading, tags }
    }
  } = useSelector((state) => state)
  const content = useRef('')
  const [userTyping, setUserTyping] = useState(false)
  const [clearContent, setClearContent] = useState(false)
  const [filesToUpload, setFilesToUpload] = useState([])
  const [inputError, setInputError] = useState('')
  const [errorFromInputArea, setErrorFromInputArea] = useState('')
  const [isInvalidConversation, setIsInvalidConversation] = useState(false)
  const [sendTriggered, setSendTriggered] = useState(false)
  const [fileTuple, setFileTuple] = useState([[], []])
  // const [focusedMode, setFocusedMode] = useState(false)
  //created contentLength state to show Send button if content is not empty(mobile). content.current ref does not reflect immediately, so using state for this purpose
  const [contentLength, setContentLength] = useState(0)

  useEffect(() => {
    if (openedConversation) {
      const isOne2OneConv = openedConversation.active_users.length === 2
      if (isOne2OneConv) {
        const inValidUser = openedConversation.active_users.find((user) => user.status !== 'active')
        if (inValidUser && inValidUser.id > 0) {
          setIsInvalidConversation(true)
        } else {
          setIsInvalidConversation(false)
        }
      } else {
        setIsInvalidConversation(false)
      }
    }
  }, [openedConversation])

  useEffect(() => {
    // for plain messages
    if (pendingMessages?.length > 0) {
      const unProcessedMessages = pendingMessages.filter((message) => {
        return !message.requested && !message.hasFiles
      })

      unProcessedMessages.forEach((message) => {
        dispatch(
          messengerActions.postMessage(
            message.parentObjectId,
            message.content,
            message.originMessageId,
            null,
            message.messageId
          )
        )
      })

      ///process the messages with files. This code will be executed for messages with tags
      if (
        !tagsLoading &&
        sendTriggered &&
        fileTuple?.length > 0 &&
        (fileTuple[0].length > 0 || fileTuple[1].length > 0)
      ) {
        const messagesWithTags = pendingMessages.filter((m) => {
          return m.tag?.length > 0 && !m.processed
        })

        for (const messageWithTag of messagesWithTags) {
          if (fileTuple[0].length > 0) {
            //if there are images
            proceesImagesWithIK(fileTuple[0], messageWithTag.tag)
          }

          //if there are documents
          if (fileTuple[1].length > 0) {
            let startingTagIndex = 1
            if (fileTuple[0].length > 0) {
              //if there are images in post, then tag index for document will be after count of images
              startingTagIndex = startingTagIndex + fileTuple[0].length
            }
            dispatch(
              commonActions.complexFilesCreation(
                fileTuple[1],
                messageWithTag.tag,
                1,
                'document,compressed_file',
                startingTagIndex
              )
            )
          }
          dispatch(messengerActions.updateProcessedTag(messageWithTag.tag))
        }
        setSendTriggered(false)
        setFileTuple[[[], []]]
      }
    }
  }, [JSON.stringify(pendingMessages), sendTriggered, tagsLoading])

  // Save draft message
  useEffect(() => {
    if (!isEditMode) {
      if (content.current.length === 0 && userTyping) {
        if (
          openedConversation.conversation.id > 0 &&
          content.current !== openedConversation.content.message_draft.message
        ) {
          dispatch(
            messengerActions.saveDraftMessage(
              openedConversation.conversation.id,
              originMessage && originMessage.message && originMessage.message.id,
              content.current
            )
          )
        }
      }
      const draftTimer = setTimeout(() => {
        if (content.current && content.current.length > 0 && userTyping) {
          if (
            openedConversation.conversation.id > 0 &&
            content.current !== openedConversation.content.message_draft.message
          ) {
            dispatch(
              messengerActions.saveDraftMessage(
                openedConversation.conversation.id,
                originMessage && originMessage.message && originMessage.message.id,
                content.current
              )
            )
          }
        }
      }, appConstantSettings.SAVE_DRAFT_MESSAGE_TIMOUT)
      return () => clearTimeout(draftTimer)
    }
  }, [content.current])

  useEffect(() => {
    if (inputError.length > 0) {
      toast.custom(
        (toastProps) => (
          <Alert text={inputError} type='error' onRemoveAlert={handleRemoveAlert} toastProps={toastProps} />
        ),
        {
          duration: 1500
        }
      )
    }
  }, [inputError])

  const sendMessage = async (e) => {
    setInputError('')
    setSendTriggered(true)
    const tempMessageId = uuid()
    setErrorFromInputArea('')
    if (openedConversation?.conversation?.id > 0) {
      const messageObject = {
        parentObjectId: openedConversation.conversation.id,
        messageId: tempMessageId,
        content: content.current,
        originMessageId: originMessage?.message?.id,
        requested: false,
        hasFiles: filesToUpload.length > 0
      }
      dispatch(messengerActions.addToPendingMessages(messageObject))

      //Do not show reply block once message with files is in process.
      //It would be weird to show processing message with reply block
      dispatch(messengerActions.setOriginMessage(null))
      if (filesToUpload.length > 0) {
        try {
          const [images, documents] = separateFilesByType(filesToUpload)
          setFileTuple([images, documents])
          dispatch(
            messengerActions.createBackgroundTagMsgr(
              messageObject.parentObjectId,
              images.length,
              documents.length,
              messageObject.originMessageId,
              messageObject.content,
              tempMessageId
            )
          )

          setFilesToUpload([])
          content.current = ''
          setClearContent(true)
        } catch (error) {
          setInputError(error?.message)
        }
      } else if (content.current.length > 0) {
        content.current = ''
        setClearContent(true)
      } else {
        setInputError(t('messenger.clientErrors.emptyMessageError'))
      }
    }
    // }
    setFocusedMode(false)
    if (e && e.target) {
      e.target.blur()
    }
  }
  const handleRemoveAlert = () => {
    setInputError('')
    setErrorFromInputArea('')
  }
  const handleErrorFromInputArea = (error) => {
    setErrorFromInputArea(error)
    if (error?.length > 0 && error !== t('messenger.clientErrors.emptyMessageError')) {
      toast.custom(
        (toastProps) => <Alert text={error} type='error' onRemoveAlert={handleRemoveAlert} toastProps={toastProps} />,
        {
          duration: 1500
        }
      )
    }
  }

  const handleFiles = (newFiles) => {
    setClearContent(false)
    setFilesToUpload(newFiles)
    //dispatch(messengerActions.hasMessageFiles(newFiles.length > 0))
  }
  const handleReturn = (e) => {
    if (e) {
      handleEdit(content.current)
      return true
    }
    sendMessage()
    return true
  }
  // const handleEditReturn = (e) => {
  //   handleEdit(content.current)
  //   return true
  // }

  const handleChange = (currentContent, imagesToUpload) => {
    setClearContent(false)
    content.current = currentContent
    setContentLength(currentContent.length)
    //setContent(currentContent)
    setUserTyping(true)
  }
  const getFileError = (fileError) => {
    setInputError(fileError)
  }
  const hasTextBlur = content.current.length > 0 || filesToUpload.length > 0

  return isInvalidConversation ? (
    <ErrorMessage center={true} className={styles.deactivatedUserError} text={t('global.invalidUserMessage')} />
  ) : (
    <React.Fragment>
      {originMessage && (
        <div className={styles.replyingContainer}>
          <ReplyingToMessage
            messageObj={originMessage}
            originMessageContainerCSS={styles.originMessageContainerCSS}
            scrollToTarget={scrollToTarget}
          />
          {!isMobile && <div className={styles.adjustingDiv}></div>}
        </div>
      )}
      <div
        data-typeid={openedConversation.conversation.id}
        data-type='conversation'
        className={`${appConstantSettings.DIV_TAG_CONTAINER}_WRITE_MESSAGE ${styles.container} ${
          isEditMode && styles.editContainer
        }`}>
        <div
          className={`${styles.editorContainer} ${(focusedMode || originMessage) && styles.showToolbar} ${
            focusedMode && filesToUpload.length > 0 && styles.editorImageContainer
          }`}>
          <RichTextEditor
            parentObjectId={openedConversation.conversation.id}
            isEditMode={isEditMode}
            value={isEditMode ? existingMessage : openedConversation.content.message_draft.message || ''}
            showTextFormatter={true}
            showEmojiPicker={true}
            showFilePicker={isEditMode ? false : true}
            placeholder={t('messenger.inputPlaceholder')}
            clearEditor={clearContent}
            handleChange={handleChange}
            handleReturn={handleReturn}
            handleFiles={handleFiles}
            handleEscape={handleExitEditMode}
            allowedImageTypes={messengerSettings.ACCEPTED_IMAGE_TYPES}
            allowedImageSizeInMB={messengerSettings.MAX_IMAGE_SIZE_MB}
            allowedDocumentTypes={messengerSettings.ACCEPTED_DOCUMENT_TYPES}
            allowedDocumentSizeInMB={messengerSettings.MAX_DOC_SIZE_MB}
            filesLimit={messengerSettings.FILES_LIMIT}
            getFileError={getFileError}
            containerCSS={`${styles.richTextAreaWrapper} ${
              !isEditMode && !focusedMode && !originMessage && styles.hasNotFocusedMode
            }`}
            charLimit={messengerSettings.MAX_CHAR_COUNT}
            maxLines={messengerSettings.MAX_LINES}
            handleInputError={handleErrorFromInputArea}
            editorRef={isEditMode ? editEditorRef : editorRef}
            editorId={isEditMode ? editEditorId : `newMessage`}
            errorMessageLimitExceed={t('global.messageLimit', {
              limit: messengerSettings.MAX_CHAR_COUNT
            })}
            errorMessageRequired={t('messenger.clientErrors.emptyMessageError')}
            fromMessenger={true}
            allowDocs={false}
            activeUsers={tagUsersList}
            handleFocusMode={setFocusedMode}
            keepFormatOnPopulate={isEditMode}
            originMessage={originMessage}
            focusedMode={focusedMode}
            // activeUsers={openedConversation && openedConversation.active_users}
          />
        </div>

        {!isEditMode ? (
          (contentLength > 0 || filesToUpload.length > 0 || !isMobile || loadingSendMessage) && (
            <button
              type='button'
              //disabled={content.length === 0 && filesToUpload.length === 0}
              //disabled={loadingSendMessage}
              aria-label={loadingSendMessage ? t('messenger.sendMessageLoading') : t('messenger.sendMessage')}
              ref={sendButtonRef}
              className={`${parentStyles.transparentButton} ${styles.sendButton} ${
                !focusedMode && hasTextBlur && styles.hasTextBlur
              }`}
              onClick={sendMessage}>
              <Icon
                name='send-copy'
                styleModifier={`${styles.sendIcon} ${!focusedMode && hasTextBlur && styles.hasSendIconBlur}`}
              />
            </button>
          )
        ) : (
          <div className={styles.editButtonContainer}>
            <IconButton
              iconName='close24'
              className={`${styles.editButtons} ${styles.closeEdit}`}
              onClick={handleExitEditMode}
            />
            {loadingEditMessage ? (
              <div className={`${styles.editButtons} ${styles.edit}`}>
                <Spinner customCss={styles.spinner} isWhite={true} />
              </div>
            ) : (
              <IconButton
                iconName='check24'
                className={`${styles.editButtons} ${styles.edit}`}
                onClick={() => handleEdit(content.current)}
              />
            )}
          </div>
        )}
      </div>
      {isMobile ? (
        <Toaster
          position='bottom-center'
          containerStyle={{
            bottom: errorFromInputArea && errorFromInputArea.length > 0 ? 165 : 115
          }}
        />
      ) : (
        <Toaster
          position='bottom-left'
          containerStyle={{
            position: 'absolute',
            left: 115,
            bottom: errorFromInputArea && errorFromInputArea.length > 0 ? 165 : 115
          }}
        />
      )}
    </React.Fragment>
  )
}
